// ══════════════════════════════════════════════════════════════════════════════
// SELDOR — Milestone Gating Service
// The "Metric Gate" that keeps higher-level milestones locked until:
//   1. Their parent milestone is completed (prerequisite chain)
//   2. The metric has been met for N CONSECUTIVE MONTHS
// ══════════════════════════════════════════════════════════════════════════════

/**
 * Determine whether a milestone is unlocked and whether it can be completed.
 *
 * Three states possible:
 *   • 'locked'      — prerequisites not met (parent incomplete)
 *   • 'in_progress' — prerequisites met, but consecutive months streak not yet achieved
 *   • 'ready'       — prerequisites met AND streak achieved → can be marked complete
 *   • 'completed'   — already done
 *
 * @param {Object} milestone      the milestone row
 * @param {Array}  allMilestones  all tenant's milestones (for parent lookup)
 * @returns {Object} { state, reason_ar, reason_en, blockingParent, monthsRemaining }
 */
export function evaluateMilestoneGate(milestone, allMilestones = []) {
  if (milestone.is_completed) {
    return {
      state: 'completed',
      reason_ar: 'مكتمل',
      reason_en: 'Completed',
      blockingParent: null,
      monthsRemaining: 0
    };
  }

  // ─── Gate 1: Parent prerequisite ─────────────────────────────────────
  if (milestone.parent_milestone_id) {
    const parent = allMilestones.find(m => m.id === milestone.parent_milestone_id);
    if (parent && !parent.is_completed) {
      return {
        state: 'locked',
        reason_ar: `يتطلب إكمال: ${parent.title_ar}`,
        reason_en: `Requires: ${parent.title_en}`,
        blockingParent: parent,
        monthsRemaining: 0
      };
    }
  }

  // ─── Gate 2: Consecutive months streak ───────────────────────────────
  const required = parseInt(milestone.consecutive_months_required || 0);
  const achieved = parseInt(milestone.consecutive_months_achieved || 0);

  if (required > 0 && achieved < required) {
    const remaining = required - achieved;
    return {
      state: 'in_progress',
      reason_ar: `يحتاج ${remaining} شهر متتالي بتحقيق الهدف (${achieved}/${required})`,
      reason_en: `Needs ${remaining} more consecutive month(s) meeting target (${achieved}/${required})`,
      blockingParent: null,
      monthsRemaining: remaining,
      streakProgress: achieved / required
    };
  }

  // All gates passed
  return {
    state: 'ready',
    reason_ar: 'مستعد للإكمال',
    reason_en: 'Ready to complete',
    blockingParent: null,
    monthsRemaining: 0
  };
}

/**
 * Batch evaluator — enriches all milestones with gate state.
 * Used by GrowthRoadmapPage to render each card with correct lock/ready UI.
 */
export function evaluateAllGates(milestones) {
  return milestones.map(m => ({
    ...m,
    gate: evaluateMilestoneGate(m, milestones)
  }));
}

/**
 * Is a milestone actually completable right now?
 * True if state === 'ready'. Used to disable/enable "Mark Complete" button.
 */
export function canMarkComplete(milestone, allMilestones) {
  const gate = evaluateMilestoneGate(milestone, allMilestones);
  return gate.state === 'ready';
}

/**
 * Count milestones by gate state — for roadmap summary UI.
 */
export function summarizeGateStates(milestones) {
  const summary = { completed: 0, ready: 0, in_progress: 0, locked: 0 };
  for (const m of milestones) {
    const gate = m.gate || evaluateMilestoneGate(m, milestones);
    summary[gate.state] = (summary[gate.state] || 0) + 1;
  }
  return summary;
}
